import { supabase } from './supabase';
import type { LetterTemplateData } from '../letter-templates/types';
import { defaultDataFor } from '../letter-templates';

function hydrate(td: any | null): LetterTemplateData | null {
  if (!td || Object.keys(td).length === 0) return null;
  const base = defaultDataFor(td.template_id || 'classic_letter');
  return { ...base, ...td, template_id: td.template_id || base.template_id, format: (td.format ?? base.format) as any };
}

export async function loadDesignDefaults(agentId: string): Promise<LetterTemplateData | null> {
  const { data, error } = await supabase
    .from('doorsteps_agent_settings')
    .select('design_defaults')
    .eq('agent_id', agentId)
    .maybeSingle();
  if (error || !data) return null;
  return hydrate(data.design_defaults);
}

export async function saveDesignDefaults(
  agentId: string,
  data: LetterTemplateData
): Promise<void> {
  const { error } = await supabase
    .from('doorsteps_agent_settings')
    .upsert(
      {
        agent_id: agentId,
        design_defaults: data,
        updated_at: new Date().toISOString(),
      },
      { onConflict: 'agent_id' }
    );
  if (error) throw error;
}

export async function loadTemplateData(campaignId: string): Promise<LetterTemplateData | null> {
  const { data, error } = await supabase
    .from('doorsteps_campaigns')
    .select('template_id, format, template_data')
    .eq('id', campaignId)
    .maybeSingle();
  if (error || !data) return null;
  const td = data.template_data as Partial<LetterTemplateData> | null;
  if (!td || Object.keys(td).length === 0) return null;
  // Backfill missing fields from template defaults so the form is always complete.
  const base = defaultDataFor(td.template_id || data.template_id || 'classic_letter');
  return { ...base, ...td, template_id: td.template_id || base.template_id, format: (td.format ?? data.format ?? base.format) as any };
}

export async function saveTemplateData(
  campaignId: string,
  data: LetterTemplateData
): Promise<void> {
  const { error } = await supabase
    .from('doorsteps_campaigns')
    .update({
      template_id: data.template_id,
      format: data.format,
      template_data: data,
    })
    .eq('id', campaignId);
  if (error) throw error;
}

/**
 * Upload a hero photo / agent headshot to the doorsteps-letters bucket.
 * Path is `{agent_id}/{campaign_id}/{filename}` so RLS can scope writes.
 */
export async function uploadLetterAsset(
  agentId: string,
  campaignId: string,
  file: File
): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
  const path = `${agentId}/${campaignId}/${Date.now()}.${ext}`;
  const { data, error } = await supabase.storage
    .from('doorsteps-letters')
    .upload(path, file, { cacheControl: '3600', upsert: false });
  if (error) throw error;
  const { data: pub } = supabase.storage.from('doorsteps-letters').getPublicUrl(data.path);
  return pub.publicUrl;
}
